import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DataTableSort } from '@ui/chart/DataTable';
import KpiStrip from '@ui/chart/KpiStrip';
import Message from '@ui/message/Message';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import { useChannelEvolution, useChannelGroups, useChannelReport } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import BiSwitchRow from '../fragment/BiSwitchRow';
import ChannelGroupsDrawer, { ChannelGroupsDrawerHandle } from '../fragment/ChannelGroupsDrawer';
import { channelBuildUp, ChannelEvolutionCard, ChannelExportMenu, ChannelMixCard, ChannelTableCard } from '../fragment/ChannelCards';
import {
	buildTree, CHANNEL_BLOCK_IDS, VIEW_INPUT, columnsOf, DEFAULT_CHANNEL_BLOCKS, EvolutionMeasure, exportBlocks, exportSort, MIX_COMPARISON_INPUT, MIX_MEASURE_INPUT,
	MixComparison, MixMeasure, nodeOf, tableRows,
} from '../fragment/Channels.utilities';
import BiBookingsDrawer, { BiBookingsDrawerHandle, BiBookingsRequest } from '../fragment/BiBookingsDrawer';
import { bookingCellOf, cutLabel } from '../fragment/BiBookings.utilities';
import { topRowOrder } from '../fragment/RatePlans.utilities';
import { evolutionFileName, formatDateTime, periodPresets, propertyCount, revenueOf, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';
import useColumnSets from '../useColumnSets';
import useBiView, { BiView } from '../useBiView';
import BiViewBy from '../fragment/BiViewBy';

// The Channels report (BI report 5, ROO2-285, direction A of the canvas of 2026-10-23: the Rate Plans order). Which booking
// channels bring the business of the hotel, against last year. Spec: "Channels Feature Specification v1.3"; decisions D1–D12
// and Q1–Q10 of the grill of the mock, and the grill of ROO2-285 (2026-10-26).
const BiChannelsPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const [blocks, toggleBlock] = useColumnSets('CHANNELS', CHANNEL_BLOCK_IDS, DEFAULT_CHANNEL_BLOCKS);
	const width = useBiWidthClass();
	const [mixMeasure, setMixMeasure] = useState<MixMeasure>('rev');
	const [comparison, setComparison] = useState<MixComparison>('stly');
	const [measure, setMeasure] = useState<EvolutionMeasure>('rn');
	const [windowDays, setWindowDays] = useState(30);
	const toaster = useToaster();
	const [exporting, setExporting] = useState(false);
	const enabled = scope.data !== undefined && address.ready;
	const report = useChannelReport(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, enabled);
	const evolution = useChannelEvolution(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, windowDays,
		address.revenueScope, address.vat, enabled);
	const groups = useChannelGroups();
	const editor = useRef<ChannelGroupsDrawerHandle>(null);
	const bookings = useRef<BiBookingsDrawerHandle>(null);
	const chartRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLDivElement>(null);
	const [focusOf, setFocusOf] = useState<{ key: string, id: string } | null>(null);
	const [sortOf, setSortOf] = useState<{ key: string, sort: DataTableSort | undefined } | null>(null);
	const { first, last } = address;
	const presets = useMemo(() => periodPresets(address.asOfMonth, first, last), [address.asOfMonth, first, last]);
	const s = scope.data;
	const all = address.companyHotelId === null;
	const scopeR = address.revenueScope, vat = address.vat;
	const data = report.data;
	const savedGroups = groups.data?.groups;
	const hasGroups = (savedGroups?.length ?? 0) > 0;
	// View by (Greg, 2026-10-28): Groups puts the keys of a Channel Group into its row; Channels shows one row per channel key, as
	// a company with no group. Every card reads the tree, so every card follows the switch; the totals do not change (RQ-04).
	const { view, setView } = useBiView('CHANNELS', hasGroups);
	const tree = useMemo(() => data === undefined || savedGroups === undefined ? undefined
		: buildTree(data, view === 'GROUPS' ? savedGroups : [], scopeR, vat), [data, savedGroups, view, scopeR, vat]);
	const buildUp = useMemo(() => {
		const lookup = channelBuildUp(evolution.data, tree, measure, address.period);
		return lookup === null ? undefined : { block: `Build-up ${windowDays}D`, label: lookup.label, of: lookup.of, total: lookup.total };
	}, [evolution.data, tree, measure, windowDays, address.period]);

	if (scope.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Channels report did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (s === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	const withData = address.properties.some(p => p.reservationsSource !== null);
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	const sublabel = all ? `${propertyCount(address.properties.length)} · Channels` : `${address.property?.area ?? ''} · Channels`;
	// The name of the scope in the menu and the file name: the property, the Property group, or null (All properties).
	const scopeName = address.property !== null ? shortName(address.property, s.companyName) : (address.propertyGroup?.title ?? null);
	const loading = data === undefined || groups.data === undefined;
	const state = report.isError || groups.isError ? 'error' as const : (loading ? 'loading' as const : (report.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const evolutionState = evolution.isError ? 'error' as const
		: (evolution.data === undefined ? 'loading' as const : (evolution.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const reasonOf = (e: unknown): string | null => typeof e === 'string' ? e : (e instanceof Error ? e.message : null);
	const nameOf = (id: number) => {
		const p = s.properties.find(x => x.companyHotelId === id);
		return p === undefined ? String(id) : shortName(p, s.companyName);
	};
	const readProperties = data === undefined ? 0 : new Set(data.parts.map(p => p.companyHotelId)).size;
	const scopeLine = tree === undefined ? null
		: `${tree.rows.length} channels · ${all ? `${readProperties} of ${propertyCount(address.properties.length)}` : nameOf(address.companyHotelId as number)}`
			+ `${view === 'GROUPS' && hasGroups ? ` · ${savedGroups?.length} channel groups` : ''}`;
	// Like-for-like (ROO2-271 D1): the properties with no stays in the last-year period stay out of Δ%.
	const missingNames = data === undefined ? [] : data.lastYearMissing.map(nameOf);
	const lfl = missingNames.length > 0;
	const leftOut = `${missingNames.length === 1 ? 'a property' : `${missingNames.length} properties`} with no stays in the last-year period: ${missingNames.join(', ')}`;
	const lastYearNote = !lfl ? null : `Like-for-like = today without ${leftOut}. TODAY LFL RN and TODAY LFL REV show it; Δ% compares it with last year.`;
	const vsStly = lfl ? 'vs STLY, like-for-like' : 'vs STLY';
	const total = tree?.total;
	const rev = (f: Parameters<typeof revenueOf>[0]) => revenueOf(f, scopeR, vat);
	const ratio = (a: number | null, b: number) => a === null || b === 0 ? null : a / b;
	const chg = (now: number | null, then: number | null) => now === null || then === null || then === 0 ? undefined : { value: (now - then) / then, format: 'percent' as const, label: vsStly };
	const kpis = total === undefined || state === 'error' ? [
		{ id: 'rev', label: 'Revenue', value: null, format: 'money' as const },
		{ id: 'rn', label: 'Room nights', value: null, format: 'number' as const },
		{ id: 'adr', label: 'ADR', value: null, format: 'money' as const, decimals: 2 },
		{ id: 'channels', label: 'Channels', value: null, format: 'number' as const },
	] : [
		{ id: 'rev', label: 'Revenue', value: rev(total.cuts[0]), format: 'money' as const, change: chg(rev(total.comparable), rev(total.stly)) },
		{ id: 'rn', label: 'Room nights', value: total.cuts[0].roomNights, format: 'number' as const, change: chg(total.comparable.roomNights, total.stly.roomNights) },
		{ id: 'adr', label: 'ADR', value: ratio(rev(total.cuts[0]), total.cuts[0].roomNights), format: 'money' as const, decimals: 2,
			change: chg(ratio(rev(total.comparable), total.comparable.roomNights), ratio(rev(total.stly), total.stly.roomNights)) },
		{ id: 'channels', label: 'Channels', value: tree?.rows.length ?? null, format: 'number' as const },
	];
	// A switch of View by also clears the focus and the sort: a group id has no row in the Channels view.
	const resetKey = `${address.companyHotelId ?? `all${address.propertyGroupId ?? ''}`}|${address.period.from}|${address.period.to}|${view}`;
	const focus = focusOf?.key === resetKey ? focusOf.id : null;
	const sort = sortOf?.key === resetKey ? sortOf.sort : undefined;
	// RQ-13 and RQ-17: the chart lines and the mix rows follow the current order of the table.
	const order = tree === undefined ? [] : (sort === undefined ? tree.rows.map(r => r.id)
		: topRowOrder(tableRows(tree, scopeR, vat, buildUp?.of), sort, columnsOf(blocks, buildUp ?? null, lfl).columns));
	const onFocus = (id: string | null) => setFocusOf(id === null ? null : { key: resetKey, id });
	// The key alone would bring an old focus or sort back on a switch back to the first view.
	const onView = (next: BiView) => {
		setView(next);
		setFocusOf(null);
		setSortOf(null);
	};
	// RQ-15: a click on a name focuses the chart; a second click on the same name returns to all lines.
	const onSelect = (row: { id: string }) => {
		if (row.id === focus) { onFocus(null); return; }
		onFocus(row.id);
		chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};
	// RQ-19: a click on a bar highlights its row in the table (and focuses the chart on it, as a click on the name).
	const onPick = (id: string) => { onFocus(id); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
	// B8: the Export menu of the mix and of the table. "Current view" sends every selection of the card (the server rebuilds it
	// from the report and the saved groups); "All data" the scope and the stay months. Export waits for the report and the groups.
	const exportReady = data !== undefined && groups.data !== undefined && !report.isPlaceholderData && !report.isError;
	const onExport = (card: 'TABLE' | 'MIX') => (file: 'CURRENT_VIEW' | 'ALL_DATA') => {
		setExporting(true);
		const request = { companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from, stayTo: address.period.to };
		const order = exportSort(sort);
		const download = file === 'ALL_DATA' ? BiService.downloadChannelExcel({ ...request, file })
			: BiService.downloadChannelExcel(card === 'TABLE'
				? { ...request, file, card, view: VIEW_INPUT[view], scope: scopeR, vat, ...order, columns: exportBlocks(blocks) }
				: { ...request, file, card, view: VIEW_INPUT[view], scope: scopeR, vat, ...order, measure: MIX_MEASURE_INPUT[mixMeasure], comparison: MIX_COMPARISON_INPUT[comparison] });
		const name = file === 'ALL_DATA' ? 'All data' : `Current view ${card === 'TABLE' ? 'table' : 'mix'}`;
		download
			.then(blob => saveFile(blob, evolutionFileName(scopeName, name, address.period, s.asOf, 'Channels')))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};
	const exportMenu = (card: 'TABLE' | 'MIX', rowWord: string) => <ChannelExportMenu scopeName={scopeName ?? 'All properties'} period={address.period}
		ready={exportReady} exporting={exporting} onExport={onExport(card)} rowWord={rowWord} />;
	const canEdit = groups.data?.canEdit === true;
	// ROO2-284: a click on RN or REV of a cut opens the bookings behind the number: the keys of the row (a channel, the keys of
	// a group) or one raw name, at the cut of the column.
	const onCellClick = (row: { id: string }, columnId: string) => {
		const cell = bookingCellOf(columnId);
		const node = tree === undefined ? null : nodeOf(tree, row.id);
		if (cell === null || node === null) return;
		const name = node.kind === 'NAME' ? (node.name ?? null) : null;
		bookings.current?.open({
			report: 'CHANNELS', companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from,
			stayTo: address.period.to, cut: cell.cut, measure: cell.measure, keys: node.keys, name, rowTitle: node.title,
			columnTitle: `${cutLabel(cell.cut)} ${cell.measure === 'rn' ? 'RN' : 'REV'}`,
		});
	};
	const bookingsExport = (request: BiBookingsRequest, ready: boolean) => <ChannelExportMenu scopeName={scopeName ?? 'All properties'} period={address.period}
		ready={ready} exporting={exporting} rowWord="list" onExport={file => {
			setExporting(true);
			BiService.downloadBookingsExcel({ report: request.report, companyHotelId: request.companyHotelId, propertyGroupId: request.propertyGroupId,
				stayFrom: request.stayFrom, stayTo: request.stayTo, cut: request.cut, keys: request.keys, name: request.name, row: request.rowTitle, scope: scopeR,
				vat, file })
				.then(blob => saveFile(blob, evolutionFileName(scopeName, `Bookings ${request.rowTitle} ${request.columnTitle}`, address.period, s.asOf, 'Channels')))
				.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
				.finally(() => setExporting(false));
		}} />;

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Channels" fullWidthSwitch>
			{address.importedAt !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{formatDateTime(address.importedAt)}</span></span>}
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			{!withData && <Message state="info" title="No reservations for this scope" flush>
				<p>The Channels report reads the channels of the imported reservations. Connect a Reservations integration in{' '}
					<Link to={all ? '/secured/settings/integrations' : `/secured/settings/integrations/${address.companyHotelId ?? ''}#reservations`}
						className="font-medium underline">Settings › Integrations</Link>.</p>
			</Message>}
			{withData && <>
				<BiSwitchRow scope={scopeR} vat={vat} onScope={v => address.set({ revenue: v === 'TOTAL' ? 'total' : 'room' })}
					onVat={v => address.set({ vat: v === 'NET' ? 'net' : 'incl' })} period={address.period}
					onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last}
					line={scopeLine} sources={sources}>
					{/* P9: "Edit channel groups" edits what View by: Groups shows, so it sits in this frame, as in Segmentation. */}
					<BiViewBy value={view} onChange={onView} hasGroups={hasGroups} singleLabel="Channels" label="Rows of the report"
						onEdit={canEdit ? () => editor.current?.open() : null} editLabel="Edit channel groups" />
				</BiSwitchRow>
				<div aria-busy={state === 'refreshing'} className={`transition-opacity${state === 'refreshing' ? ' opacity-50' : ''}`}>
					<KpiStrip ariaLabel="Channels: headline figures" items={kpis} state={state === 'loading' ? 'loading' : 'default'} />
					{lfl && <p className="mt-2 text-xs text-gray-500">The changes are like-for-like: they leave out {leftOut}.</p>}
				</div>
				<ChannelMixCard tree={tree} order={order} period={address.period} measure={mixMeasure} onMeasure={setMixMeasure} comparison={comparison}
					onComparison={setComparison} scope={scopeR} vat={vat} state={state} onRetry={() => { void report.refetch(); void groups.refetch(); }}
					error={reasonOf(report.error)} lastYearNote={lastYearNote} onPick={onPick} exportMenu={exportMenu('MIX', 'chart')} />
				<div ref={tableRef} className="scroll-mt-4">
					<ChannelTableCard tree={tree} period={address.period} blocks={blocks} onToggle={toggleBlock} scope={scopeR} vat={vat} state={state}
						onRetry={() => { void report.refetch(); void groups.refetch(); }} resetKey={resetKey} lastYearNote={lastYearNote} lfl={lfl}
						error={reasonOf(report.error)} exportMenu={exportMenu('TABLE', 'table')} buildUp={buildUp} onSelect={onSelect} selectedId={focus ?? undefined}
						onSortChange={next => setSortOf({ key: resetKey, sort: next })} onCellClick={onCellClick} />
				</div>
				<ChannelEvolutionCard ref={chartRef} evolution={evolution.data} tree={tree} period={address.period} measure={measure} onMeasure={setMeasure}
					window={windowDays} onWindow={setWindowDays} state={evolutionState} error={reasonOf(evolution.error)} onRetry={() => void evolution.refetch()}
					order={order} focus={focus} onFocus={onFocus} />
			</>}
		</main>
		<BiBookingsDrawer ref={bookings} scope={scopeR} vat={vat} manyProperties={all} propertyName={nameOf} exportMenu={bookingsExport} />
		{groups.data !== undefined && canEdit && <ChannelGroupsDrawer ref={editor} data={groups.data} companyName={s.companyName} propertyCount={s.companyProperties} />}
	</>;
};

export default BiChannelsPage;
